export interface Achievement {
  id: string;
  title: string;
  description: string;
  category: 'documentation' | 'consistency' | 'engagement' | 'milestones';
  icon: string;
  points: number;
  requirement: {
    type: 'streak' | 'count' | 'percentage' | 'days' | 'special';
    target: number;
    metric: string;
  };
  earned: boolean;
  earnedDate?: string;
  progress?: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface UserStats {
  totalHours: number;
  totalShifts: number;
  perfectDays: number;
  currentStreak: number;
  longestStreak: number;
  photosCaptured: number;
  achievementsEarned: number;
  totalPoints: number;
  weeksSinceStart: number;
  averageHoursPerWeek: number;
  disputesDocumented: number;
  rightsModulesCompleted: number;
}

export const achievements: Achievement[] = [
  // Documentation Achievements
  {
    id: 'first_photo',
    title: 'First Snapshot',
    description: 'Capture your first workplace photo',
    category: 'documentation',
    icon: 'camera',
    points: 50,
    requirement: { type: 'count', target: 1, metric: 'photos' },
    earned: false,
    rarity: 'common'
  },
  {
    id: 'photo_pro',
    title: 'Photo Pro',
    description: 'Capture 50 workplace photos',
    category: 'documentation',
    icon: 'camera',
    points: 200,
    requirement: { type: 'count', target: 50, metric: 'photos' },
    earned: false,
    rarity: 'rare'
  },
  {
    id: 'documentation_master',
    title: 'Documentation Master',
    description: 'Capture 200 workplace photos',
    category: 'documentation',
    icon: 'shield',
    points: 500,
    requirement: { type: 'count', target: 200, metric: 'photos' },
    earned: false,
    rarity: 'epic'
  },

  // Consistency Achievements
  {
    id: 'perfect_day',
    title: 'Perfect Day',
    description: 'Document all clock-ins and breaks for a full day',
    category: 'consistency',
    icon: 'star',
    points: 75,
    requirement: { type: 'count', target: 1, metric: 'perfect_days' },
    earned: false,
    rarity: 'common'
  },
  {
    id: 'perfect_week',
    title: 'Perfect Week',
    description: 'Document every shift for 7 consecutive days',
    category: 'consistency',
    icon: 'trophy',
    points: 300,
    requirement: { type: 'streak', target: 7, metric: 'perfect_days' },
    earned: false,
    rarity: 'rare'
  },
  {
    id: 'perfect_month',
    title: 'Perfect Month',
    description: 'Document every shift for 30 consecutive days',
    category: 'consistency',
    icon: 'crown',
    points: 1000,
    requirement: { type: 'streak', target: 30, metric: 'perfect_days' },
    earned: false,
    rarity: 'legendary'
  },
  {
    id: 'early_bird',
    title: 'Early Bird',
    description: 'Clock in 5+ minutes early for 10 shifts',
    category: 'consistency',
    icon: 'sunrise',
    points: 150,
    requirement: { type: 'count', target: 10, metric: 'early_clockins' },
    earned: false,
    rarity: 'common'
  },

  // Engagement Achievements
  {
    id: 'rights_scholar',
    title: 'Rights Scholar',
    description: 'Complete all worker rights education modules',
    category: 'engagement',
    icon: 'graduation-cap',
    points: 250,
    requirement: { type: 'percentage', target: 100, metric: 'rights_modules' },
    earned: false,
    rarity: 'rare'
  },
  {
    id: 'advocate',
    title: 'Worker Advocate',
    description: 'Use the dispute assistance tool',
    category: 'engagement',
    icon: 'scales',
    points: 100,
    requirement: { type: 'count', target: 1, metric: 'dispute_assistance_used' },
    earned: false,
    rarity: 'common'
  },
  {
    id: 'goal_setter',
    title: 'Goal Setter',
    description: 'Set up weekly work goals',
    category: 'engagement',
    icon: 'target',
    points: 75,
    requirement: { type: 'count', target: 1, metric: 'goals_set' },
    earned: false,
    rarity: 'common'
  },

  // Milestone Achievements
  {
    id: 'first_week',
    title: 'Welcome to Clock-It',
    description: 'Complete your first week of tracking',
    category: 'milestones',
    icon: 'calendar',
    points: 100,
    requirement: { type: 'days', target: 7, metric: 'days_active' },
    earned: false,
    rarity: 'common'
  },
  {
    id: 'hundred_hours',
    title: 'Century Club',
    description: 'Track 100 total hours',
    category: 'milestones',
    icon: 'clock',
    points: 300,
    requirement: { type: 'count', target: 100, metric: 'total_hours' },
    earned: false,
    rarity: 'rare'
  },
  {
    id: 'thousand_hours',
    title: 'Dedication Master',
    description: 'Track 1,000 total hours',
    category: 'milestones',
    icon: 'medal',
    points: 1500,
    requirement: { type: 'count', target: 1000, metric: 'total_hours' },
    earned: false,
    rarity: 'legendary'
  },
  {
    id: 'veteran',
    title: 'Clock-It Veteran',
    description: 'Use the app for 6 months',
    category: 'milestones',
    icon: 'badge',
    points: 500,
    requirement: { type: 'days', target: 180, metric: 'days_since_signup' },
    earned: false,
    rarity: 'epic'
  }
];

export class AchievementSystem {
  private static instance: AchievementSystem;

  static getInstance(): AchievementSystem {
    if (!AchievementSystem.instance) {
      AchievementSystem.instance = new AchievementSystem();
    }
    return AchievementSystem.instance;
  }

  checkAchievements(stats: UserStats): Achievement[] {
    const newlyEarned: Achievement[] = [];

    achievements.forEach(achievement => {
      if (achievement.earned) return;

      const isEarned = this.checkRequirement(achievement.requirement, stats);
      
      if (isEarned) {
        achievement.earned = true;
        achievement.earnedDate = new Date().toISOString().split('T')[0];
        newlyEarned.push(achievement);
        
        // Save to localStorage
        this.saveAchievement(achievement);
        
        // Track analytics
        this.trackAchievement(achievement);
      } else {
        // Update progress
        achievement.progress = this.calculateProgress(achievement.requirement, stats);
      }
    });

    return newlyEarned;
  }

  private checkRequirement(requirement: Achievement['requirement'], stats: UserStats): boolean {
    switch (requirement.type) {
      case 'count':
        return this.getStatValue(requirement.metric, stats) >= requirement.target;
      case 'streak':
        return stats.currentStreak >= requirement.target;
      case 'percentage':
        return this.getPercentage(requirement.metric, stats) >= requirement.target;
      case 'days':
        return this.getDaysValue(requirement.metric, stats) >= requirement.target;
      default:
        return false;
    }
  }

  private calculateProgress(requirement: Achievement['requirement'], stats: UserStats): number {
    const currentValue = this.getStatValue(requirement.metric, stats);
    return Math.min(100, (currentValue / requirement.target) * 100);
  }

  private getStatValue(metric: string, stats: UserStats): number {
    const metricMap: { [key: string]: keyof UserStats } = {
      'photos': 'photosCaptured',
      'perfect_days': 'perfectDays',
      'total_hours': 'totalHours',
      'total_shifts': 'totalShifts',
      'achievements': 'achievementsEarned',
      'disputes': 'disputesDocumented'
    };

    const key = metricMap[metric];
    return key ? stats[key] as number : 0;
  }

  private getPercentage(metric: string, stats: UserStats): number {
    if (metric === 'rights_modules') {
      return stats.rightsModulesCompleted;
    }
    return 0;
  }

  private getDaysValue(metric: string, stats: UserStats): number {
    if (metric === 'days_active') {
      return stats.weeksSinceStart * 7; // Approximate
    }
    if (metric === 'days_since_signup') {
      const signupDate = localStorage.getItem('clockit-signup-date');
      if (signupDate) {
        const diff = Date.now() - new Date(signupDate).getTime();
        return Math.floor(diff / (1000 * 60 * 60 * 24));
      }
    }
    return 0;
  }

  private saveAchievement(achievement: Achievement): void {
    const saved = JSON.parse(localStorage.getItem('clockit-achievements') || '[]');
    saved.push({
      id: achievement.id,
      earnedDate: achievement.earnedDate,
      points: achievement.points
    });
    localStorage.setItem('clockit-achievements', JSON.stringify(saved));
  }

  private trackAchievement(achievement: Achievement): void {
    // In a real app, this would send to analytics
    console.log('Achievement earned:', achievement.title);
  }

  getEarnedAchievements(): Achievement[] {
    const saved = JSON.parse(localStorage.getItem('clockit-achievements') || '[]');
    return achievements.filter(achievement => 
      saved.some((s: any) => s.id === achievement.id)
    ).map(achievement => {
      const savedData = saved.find((s: any) => s.id === achievement.id);
      return {
        ...achievement,
        earned: true,
        earnedDate: savedData.earnedDate
      };
    });
  }

  getTotalPoints(): number {
    const earned = this.getEarnedAchievements();
    return earned.reduce((total, achievement) => total + achievement.points, 0);
  }

  getNextAchievements(limit: number = 3): Achievement[] {
    return achievements
      .filter(achievement => !achievement.earned)
      .sort((a, b) => (a.progress || 0) - (b.progress || 0))
      .slice(0, limit);
  }
}

export const achievementSystem = AchievementSystem.getInstance();